import { Component, Input, OnInit, OnChanges, HostBinding, ViewEncapsulation, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { BaseComponent } from '../base/base.component';
import * as d3 from 'd3';
import * as d3Time from 'd3-time';
import * as d3TimeFormat from 'd3-time-format';
import * as d3Scale from 'd3-scale';
import * as d3Axis from 'd3-axis';
import * as d3Selection from 'd3-selection';
import * as moment from 'moment';
import { DatePipe } from '@angular/common';
import { AuthService, PatientService } from 'src/app/services';
import { ResizedEvent } from 'angular-resize-event';

@Component({
  selector: 'app-timeline',
  templateUrl: './timeline.component.html',
  styleUrls: ['./timeline.component.scss'],
})
export class TimelineComponent extends BaseComponent implements OnInit {
  @Input() dataToDisplay: any = [];
  containerSize: any;
  xScale: any;
  xAxis: any;
  yScale: any;
  yAxis: any;
  svg: any = null;
  scaleFactor = 1;
  margin: { left: number; right: number; top: number; bottom: number; };
  start: any;
  end: any;
  width: any;
  height: any;
  @Input() timelineId: any;
  poseBlock: any = [];
  continuesBlock: any;
  timelinedata: any = [];
  itemHeight;
  itemMargin;
  myArray: any = []
  selectedHour: any;
  @Input() selected: boolean;
  @Input() displayTimeline: boolean;
  @Input() graphId: any;
  bufferTime: any;

  constructor(
    private router: Router,
    public dialog: MatDialog,
    public datepipe: DatePipe,
    private patientService: PatientService,
    private auth: AuthService,
    public parentDilogRef: MatDialogRef<TimelineComponent>,
  ) {
    super();
    this.margin = { left: 30, right: 30, top: 20, bottom: 30 };
    this.width = 400 - this.margin.left - this.margin.right;
    this.height = 200 - this.margin.top - this.margin.bottom;
  }

  ngOnInit(): void {
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.poseBlock = this.dataToDisplay?.PoseBlock || []
    this.continuesBlock = this.dataToDisplay?.ContinueBlock || []
    if (this.dataToDisplay && Object.keys(this.dataToDisplay).length > 0) {
      this.svg = null;
      d3.selectAll(`#${this.timelineId}`).select("svg").remove();
      this.myArray = Object.keys(this.dataToDisplay).map(key => ({ [key]: [this.dataToDisplay[key]] }));
      this.fetchBufferTime();
      this.drawTimelines();
    }
  }

  fetchBufferTime(): void {
    if (this.timelineId && this.auth.isTokenValid()) {
      this.patientService.patientConfig(this.timelineId).subscribe((res) => {
        if (res && res.buffer_time !== undefined) {
          this.bufferTime = res.buffer_time;
          // Redraw with buffer_time now available
          this.svg = null;
          d3.selectAll(`#${this.timelineId}`).select("svg").remove();
          this.drawTimelines();
        }
      });
    }
  }

  drawTimelines(): void {
    let now = new Date(this.dataToDisplay.start_time);
    this.start = now.getTime();
    let today = new Date(this.dataToDisplay.end_time);
    this.end = today.getTime();
    this.svg = this.appendContainer();
    this.containerSize = (this.svg.node() as any).getBoundingClientRect();
    this.scaleFactor = (1 / (this.end - this.start)) * (this.containerSize.width - this.margin.left - this.margin.right);
    var graph = this.appendGraph(this.svg);
    this.xScale = d3Scale
      .scaleTime()
      .domain([this.start, this.end])
      .range([this.margin.left, this.containerSize.width - this.margin.right]);
    this.xAxis = d3Axis
      .axisBottom()
      .scale(this.xScale)
      .ticks(d3Time.timeMinute.every(30))
      .tickFormat(d3TimeFormat.timeFormat('%H:%M'))
      .tickSize(3)
      .tickPadding(5);
    this.yScale = d3Scale
      .scaleLinear()
      .domain([now, today])
      .range([this.margin.top, this.containerSize.height - this.margin.bottom]);
    this.yAxis = d3Axis.axisLeft().scale(this.yScale);
    this.svg
      .append('line')
      .attr('x1', 15)
      .attr('y1', 45)
      .attr('x2', '96.8%')
      .attr('y2', 45)
      .style('stroke', 'gray')
      .style('stroke-width', 0.5);
    this.svg
      .append('text')
      .attr('x', 5)
      .attr('y', 50)
      .attr('dy', -25)
      .attr('text-anchor', 'start')
      .attr('transform', 'rotate(-90, ' + this.margin.left + ', ' + 45 + ')')
      .text(d3TimeFormat.timeFormat('%d-%m-%Y')(now))
      .style('font-size', 9);
    this.svg
      .append('text')
      .attr('x', '99%')
      .attr('y', 50)
      .attr('dy', 0)
      .attr('text-anchor', 'end')
      .attr('transform', 'rotate(-90, ' + (this.containerSize.width - this.margin.right) + ', ' + 45 + ')')
      .text(d3TimeFormat.timeFormat('%d-%m-%Y')(today))
      .style('font-size', 9);
    const verticalLines = d3.timeMinute.every(15).range(this.start, this.end);
    verticalLines.forEach((timestamp) => {
      if (timestamp >= this.start && timestamp <= this.end) {
        const xPosition = this.xScale(timestamp);
        this.svg
          .append('line')
          .attr('x1', xPosition - 10)
          .attr('y1', this.margin.top)
          .attr('x2', xPosition - 10)
          .attr('y2', 70)
          .style('stroke', 'grey')
          .style('stroke-width', 0.5);
      }
    });
    this.svg.append('rect')
      .attr('x', 20)
      .attr('y', this.margin.top)
      .attr('width', this.containerSize.width - 60)
      .attr('height', 50)
      .style('fill', 'none')
      .style('stroke', 'lightgrey')
      .style('stroke-width', 1);
    this.svg.append('g')
      .attr("transform", 'translate(' + -10 + ',' + 70 + ')')
      .call(this.xAxis)
      .selectAll('text')
      .style('text-anchor', 'middle')
      .attr('dy', '-4px')
      .attr('transform', 'rotate(-90)')
      .attr('dx', -20)
      .attr('font-size', 9);
    this.svg.selectAll('.tick text');
    this.drawPoseBlockGraph(graph);
    this.drawContinueBlockGraph(graph);
  }

  onResized(event: ResizedEvent): void {
    if (this.dataToDisplay && Object.keys(this.dataToDisplay).length > 0) {
      this.svg = null;
      d3.select(`#${this.timelineId}`).select('svg').remove();
      this.drawTimelines();
    }
  }

  appendContainer(): any {
    if (!this.svg) {
      return d3
        .select(`#${this.timelineId}`)
        .append('svg')
        .attr('width', '100%')
        .attr('height', '103px')
        .style('margin-left', 35);
    } else {
      return this.svg;
    }
  }

  appendGraph(svg): any {
    return svg
      .append('g')
      .attr('class', 'container')
      .attr('height', this.containerSize.height)
      .datum(this.myArray);
  }

  drawPoseBlockGraph(graph): void {
    this.itemHeight = 30;
    this.itemMargin = 29;
    var rectsView = graph.append('g').attr('class', 'view');
    var yAxisMapping = {};
    let maxStack = 1;
    graph.each((data) => {
      data.forEach((timelineItem, index) => {
        if (timelineItem.PoseBlock) {
          this.timelinedata = timelineItem.PoseBlock[0];
        }
        var timelines = this.timelinedata;
        timelines.forEach((arrayLoop) => {
          arrayLoop.start_time = new Date(arrayLoop.start_time).getTime();
          arrayLoop.endtime = new Date(arrayLoop.endtime).getTime();
          if (arrayLoop.start_time < this.start) {
            arrayLoop.start_time = this.start;
          }
          if (arrayLoop.endtime > this.end) {
            arrayLoop.endtime = this.end;
          }
        });
        const getColorForPoseBlock = (d, i) => {
          if (d.posture && (d.posture === 'left')) return 'rgb(251,198,101)';
          else if (d.posture && (d.posture === 'right')) return 'rgb(255, 204, 220)';
          else if (d.posture && (d.posture === 'prone')) return 'rgb(196,164,220)';
          else if (d.posture && (d.posture === 'supine')) return 'rgb(19, 180, 255)';
          else if (d.posture && (d.posture === 'NPP')) return 'rgb(204,204,204)';
          else if (d.posture && (d.posture === 'No Data')) return 'rgb(247, 247, 205)';
          else { }
        };
        yAxisMapping[index] = maxStack;
        maxStack++;
        this.drawTimeRect(rectsView, timelines, yAxisMapping, index, getColorForPoseBlock);
      });
    });
  }

  drawContinueBlockGraph(graph): void {
    this.itemHeight = 12;
    this.itemMargin = 5;

    const ONE_MIN       = 60 * 1000;
    const bufferTimeMs  = (this.bufferTime || 0) * ONE_MIN;

    const rectsView:   any                       = graph.append('g').attr('class', 'view');
    const yAxisMapping: { [key: number]: number } = {};
    let   maxStack      = 1;

    // ── Helpers ──────────────────────────────────────────────────────────────
    const clamp = (val: any): number | null => {
      if (!val || val === 'None' || val === 'null') return null;
      const ms = typeof val === 'number' ? val : new Date(val).getTime();
      if (isNaN(ms)) return null;
      return Math.max(this.start, Math.min(this.end, ms));
    };

    const isValidPosture = (p: string | null): boolean =>
      p !== null && p !== 'No Data' && p !== 'null';

    const getColor = (d: any): string => {
      if (d.color === 'white')  return '#ffffff';
      if (d.color === 'orange') return 'orange';
      if (d.color === 'red')    return 'red';
      return 'rgb(152, 251, 152)'; // green + converted white segments
    };

    const typeOrder: Record<string, number> = { base: 0, white_mask: 1, risk: 2 };

    // ── Per-graph rendering ───────────────────────────────────────────────────
    graph.each((data: any[]) => {

      // Gather + sort PoseBlock data (already mutated to ms by drawPoseBlockGraph)
      const poseItem     = data.find(item => item.PoseBlock?.[0]);
      const poseBlockData: any[] = poseItem ? [...poseItem.PoseBlock[0]] : [];
      poseBlockData.sort((a, b) => a.start_time - b.start_time);

      // Gather + sort ContinueBlock data
      const continueBlockData: any[] = data
        .filter(item => item.ContinueBlock?.[0])
        .flatMap(item => item.ContinueBlock[0])
        .sort((a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime());

      if (poseBlockData.length === 0 && continueBlockData.length === 0) return;

      // Which posture is active at timestamp ms?
      const getPoseAt = (ms: number): string | null => {
        const found = poseBlockData.find(p => {
          const ps = typeof p.start_time === 'number' ? p.start_time : new Date(p.start_time).getTime();
          const pe = typeof p.endtime    === 'number' ? p.endtime    : new Date(p.endtime).getTime();
          return ps <= ms && ms < pe;
        });
        return found?.posture ?? null;
      };

      // Which ContinueBlock is active at timestamp ms?
      const getCBAt = (ms: number): any | null =>
        continueBlockData.find(cb => {
          const s = clamp(cb.start_time);
          const e = clamp(cb.endtime);
          return s !== null && e !== null && ms >= s && ms < e;
        }) ?? null;

      const segments: any[] = [];

      // ── STEP 1: Green base ──────────────────────────────────────────────────
      poseBlockData.forEach(pose => {
        const s = clamp(pose.start_time);
        const e = clamp(pose.endtime);
        if (s === null || e === null || s >= e) return;
        if (!isValidPosture(pose.posture))      return;
        segments.push({ start_time: s, endtime: e, color: 'green', type: 'base' });
      });

      // ── STEP 2: White overlay (grace period after risk pose changes) ────────
      let lastRiskPose: string | null = null;
      let whiteStart:   number | null = null;
      const whiteSegments: { start_time: number; endtime: number }[] = [];

      for (let t = this.start; t < this.end; t += ONE_MIN) {
        const currentPose = getPoseAt(t);
        const cb          = getCBAt(t);

        // Detect risk onset
        if (cb && lastRiskPose === null) {
          const midRisk  = clamp(cb.mid_risk_time);
          const highRisk = clamp(cb.high_risk_time);
          const hasRisk  = (midRisk  !== null && t >= midRisk) ||
                          (highRisk !== null && t >= highRisk);
          if (hasRisk) lastRiskPose = currentPose;
        }

        if (lastRiskPose === null) {
          // No risk tracked — close any stray white
          if (whiteStart !== null) {
            whiteSegments.push({ start_time: whiteStart, endtime: t });
            whiteStart = null;
          }
          continue;
        }

        const poseDiffers     = currentPose !== null && currentPose !== lastRiskPose;
        const isOverBufferTime = whiteStart !== null && (t - whiteStart) >= bufferTimeMs;

        if (poseDiffers && whiteStart === null) {
          whiteStart = t;                                               // open white
        } else if (whiteStart !== null && (!poseDiffers || isOverBufferTime)) {
          whiteSegments.push({ start_time: whiteStart, endtime: t }); // close white
          whiteStart    = null;
          lastRiskPose  = null;
        }
      }

      // Close any white still open at timeline end
      if (whiteStart !== null) {
        whiteSegments.push({ start_time: whiteStart, endtime: this.end });
      }

      // Convert closed white segments → green (historical only) or skip if No Data
      whiteSegments.forEach(seg => {
        const poseAtEnd = getPoseAt(seg.endtime - ONE_MIN);
        if (!isValidPosture(poseAtEnd)) return; // No Data / null → render nothing

        const graceElapsed = (seg.endtime - seg.start_time) >= bufferTimeMs;
        segments.push({
          start_time: seg.start_time,
          endtime:    seg.endtime,
          color: graceElapsed ? 'green'      : 'white',
          type:  graceElapsed ? 'base'       : 'white_mask',
        });
      });

      // ── STEP 3: Orange / Red overlays (highest priority) ───────────────────
      continueBlockData.forEach(cb => {
        const start = clamp(cb.start_time);
        const end   = clamp(cb.endtime);
        if (start === null || end === null || end <= start) return;

        const midRiskTime  = clamp(cb.mid_risk_time);
        const highRiskTime = clamp(cb.high_risk_time);
        const hasMidRisk   = midRiskTime  !== null && midRiskTime  < end;
        const hasHighRisk  = highRiskTime !== null && highRiskTime < end;

        if (hasMidRisk) {
          const orangeStart = Math.max(midRiskTime, start);
          const orangeEnd   = hasHighRisk ? Math.max(highRiskTime, orangeStart) : end;
          if (orangeEnd > orangeStart) {
            segments.push({
              start_time: orangeStart, endtime: orangeEnd, color: 'orange', type: 'risk',
              mid_risk_overall_time: hasHighRisk ? '' : cb.mid_risk_overall_time,
              high_risk_time: cb.high_risk_time, mid_risk_time: cb.mid_risk_time,
            });
          }
        }

        if (hasHighRisk) {
          const redStart = Math.max(highRiskTime, start);
          if (end > redStart) {
            segments.push({
              start_time: redStart, endtime: end, color: 'red', type: 'risk',
              mid_risk_overall_time: cb.mid_risk_overall_time,
              high_risk_time: cb.high_risk_time, mid_risk_time: cb.mid_risk_time,
            });
          }
        }
      });

      if (segments.length === 0) return;

      // Layer order: base → white_mask → risk
      segments.sort((a, b) => {
        const ao = typeOrder[a.type] ?? 0;
        const bo = typeOrder[b.type] ?? 0;
        return ao !== bo ? ao - bo : a.start_time - b.start_time;
      });

      yAxisMapping[0] = maxStack++;
      this.drawTimeRect(rectsView, segments, yAxisMapping, 0, getColor);
    });
  }

  drawTimeRect(view, data, yMap, index, getColor): void {
    view
      .selectAll('svg')
      .data(data)
      .enter()
      .append(() => {
        return document.createElementNS(d3Selection.namespaces.svg, 'rect');
      })
      .attr('font-size', 5)
      .attr('x', (d) => {
        return this.getXPos(d, false);
      })
      .attr('y', () => {
        return this.getStackPosition(yMap, index);
      })
      .attr('width', (d, i) => {
        return (d.endtime - d.start_time) * this.scaleFactor;
      })
      .attr('height', this.itemHeight)
      .style('fill', getColor)
      .on('click', (d, i) => {
        var selectedRect = d3Selection.select(this).node();
        var selectorLabel = 'text#' + selectedRect.id + '.textnumbers';
        var selectedLabel = d3Selection.select(selectorLabel).node();
      })
      .attr('class', (d, i) => {
        return 'timelineSeries_' + i;
      });

    view.selectAll("text.bar-text")
      .data(data)
      .enter()
      .append("text")
      .filter(d => (d.color === 'orange' && isNaN(d.high_risk_time)) || (d.color === 'red' && d.high_risk_time !== 'None' && d.mid_risk_overall_time))
      .text(d => {
        let formattedTime = d.mid_risk_overall_time;
        let [hours, minutes] = formattedTime.split(":").map(Number);
        if (hours === 0 && minutes < 60) {
          formattedTime = minutes;
        } else {
          formattedTime = formattedTime;
        }
        return formattedTime;
      })
      .attr('font-size', 5)
      .style('fill', 'white')
      .attr("id", (d, i) => `text_${i}`)
      .attr("x", d => { return this.getRiskPost(d); })
      .attr("y", d => 68)
      .attr("class", "bar-text");
  }

  getXPos(d, isNotes, index?: number, data?: any): number {
    if (isNotes) {
      return this.margin.left + (d.time - this.start) * this.scaleFactor;
    }
    return 20 + (d.start_time - this.start) * this.scaleFactor;
  }

  getStackPosition(yMap, index): number {
    return (this.containerSize.height + this.itemMargin / 2 - this.margin.bottom - (this.itemHeight + this.itemMargin) * yMap[index]);
  }

  getRiskPost(d): number {
    let compareTime = d.mid_risk_overall_time;
    let riskHoursDifference;
    let riskMinutesDifference;
    let [hours, minutes] = compareTime.split(":").map(Number);
    let midTime = new Date(d.mid_risk_time);
    let highTime = new Date(d.high_risk_time);
    const timeDifferenceMillis = highTime.getTime() - midTime.getTime();
    riskHoursDifference = Math.floor(timeDifferenceMillis / (1000 * 60 * 60));
    riskMinutesDifference = Math.floor((timeDifferenceMillis % (1000 * 60 * 60)) / (1000 * 60));
    if (riskHoursDifference > 0 && riskMinutesDifference < 30) {
      return 20 + ((d.start_time - this.start) * this.scaleFactor);
    } else if (hours === 0 && minutes < 30) {
      return 20 + ((d.start_time - this.start) * this.scaleFactor);
    } else {
      const barWidth = (d.endtime - d.start_time) * this.scaleFactor;
      return 20 + ((d.start_time - this.start) * this.scaleFactor) + barWidth / 2;
    }
  }
}